import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ClearAllOutlinedIcon from '@mui/icons-material/ClearAllOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { can } from '../../utils/permissions';


const inputSx = (colors) => ({
  minWidth: 180,
  backgroundColor: colors.primary[400],
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
});

const ExcursionAttendanceReport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const [countries, setCountries] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [countryId, setCountryId] = useState('');
  const [excursionId, setExcursionId] = useState('');

  const filteredExcursions = useMemo(() => {
    if (!countryId) return excursions;
    return excursions.filter((e) => String(e.CountryID) === String(countryId));
  }, [excursions, countryId]);
  const [departureDate, setDepartureDate] = useState('');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ totalPersons: 0, scannedPersons: 0, nonScannedPersons: 0 });
  const [filterModel, setFilterModel] = useState({ items: [] });

  const columnFilter = useMemo(() => {
    return filterModel.items.reduce((acc, item) => {
      if (item.value) acc[item.columnField] = item.value;
      return acc;
    }, {});
  }, [filterModel]);
  const columnFilterKey = JSON.stringify(columnFilter);

  const buildListParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    if (countryId) params.set('countryId', countryId);
    if (excursionId) params.set('excursionId', excursionId);
    if (departureDate) params.set('startDate', departureDate);
    if (Object.keys(columnFilter).length) params.set('filter', JSON.stringify(columnFilter));
    return params.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, countryId, excursionId, departureDate, columnFilterKey]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_APILINK}/country`, { headers: { token } });
        const data = await res.json();
        setCountries(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      }
    };
    const loadExcursions = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_APILINK}/excursion?page=1&limit=1000&sortBy=ExcursionID&sortOrder=ASC&filter={}`,
          { headers: { token } }
        );
        const data = await res.json();
        setExcursions(Array.isArray(data?.excursions) ? data.excursions : []);
      } catch (e) {
        console.error(e);
      }
    };
    loadCountries();
    loadExcursions();
  }, [token]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = buildListParams();
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/reports/excursion-attendance?${qs}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (!data.success) {
        setRows([]);
        setTotalCount(0);
        setStats({ totalPersons: 0, scannedPersons: 0, nonScannedPersons: 0 });
        setError(data.message || 'Failed to load report');
        return;
      }
      setRows(data.data || []);
      setTotalCount(data.pagination?.total ?? 0);
      setStats(data.stats || { totalPersons: 0, scannedPersons: 0, nonScannedPersons: 0 });
    } catch (e) {
      setRows([]);
      setTotalCount(0);
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [buildListParams, token]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (countryId) params.set('countryId', countryId);
      if (excursionId) params.set('excursionId', excursionId);
      if (departureDate) params.set('startDate', departureDate);
      if (Object.keys(columnFilter).length) params.set('filter', JSON.stringify(columnFilter));
      const qs = params.toString();
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/reports/excursion-attendance/export${qs ? `?${qs}` : ''}`,
        { headers: { token } }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        setError(errBody.message || `Export failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition');
      let filename = 'excursion-attendance.xlsx';
      if (dispo && dispo.includes('filename=')) {
        const m = dispo.match(/filename="?([^";]+)"?/);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setCountryId('');
    setExcursionId('');
    setDepartureDate('');
    setFilterModel({ items: [] });
    setPage(1);
  };

  const columns = [
    { field: 'serial', headerName: '#', width: 72, headerAlign: 'center', align: 'center' },
    { field: 'destination', headerName: 'Destination', flex: 1, minWidth: 130 },
    { field: 'excursion', headerName: 'Excursion', flex: 1, minWidth: 140 },
    { field: 'dateLabel', headerName: 'Date', minWidth: 150 },
    { field: 'guide', headerName: 'Guide', flex: 0.9, minWidth: 120 },
    { field: 'busNumber', headerName: 'Bus number', width: 110 },
    { field: 'passengerName', headerName: 'Passenger (Last + First)', flex: 1, minWidth: 160 },
    {
      field: 'totalPersons',
      headerName: 'Total persons',
      width: 120,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => Number(params.row.quantity || 1),
    },
    {
      field: 'scannedPersons',
      headerName: 'Scanned',
      width: 100,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => Math.max(0, Number(params.row.scanStatus ?? 0)),
      renderCell: (params) => (
        <Chip label={params.value} color="success" size="small" sx={{ fontWeight: 600, minWidth: 36 }} />
      ),
    },
    {
      field: 'notScannedPersons',
      headerName: 'Not scanned',
      width: 110,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => Math.max(0, Number(params.row.quantity || 1) - Number(params.row.scanStatus ?? 0)),
      renderCell: (params) => (
        <Chip label={params.value} color={params.value > 0 ? 'error' : 'success'} size="small" sx={{ fontWeight: 600, minWidth: 36 }} />
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Header title="EXCURSION ATTENDANCE" subtitle="Per-passenger attendance (filters + Excel export)" />
        {can('reports.excursion_attendance.export') && (
          <Button
            variant="contained"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={exporting}
            onClick={handleExport}
            sx={{ backgroundColor: colors.greenAccent[600], '&:hover': { backgroundColor: colors.greenAccent[700] } }}
          >
            {exporting ? 'Exporting…' : 'Export Excel'}
          </Button>
        )}
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start" mb={2}>
        <FormControl size="small" variant="outlined" sx={inputSx(colors)}>
          <InputLabel id="ea-country" shrink>
            Destination
          </InputLabel>
          <Select
            labelId="ea-country"
            label="Destination"
            value={countryId}
            displayEmpty
            notched
            onChange={(e) => {
              setCountryId(String(e.target.value));
              setExcursionId('');
              setPage(1);
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            inputProps={{ 'aria-label': 'Destination' }}
            renderValue={(selected) => {
              if (selected === '' || selected == null) {
                return <Box component="span" sx={{ fontStyle: 'italic', opacity: 0.92 }}>All destinations</Box>;
              }
              const c = countries.find((x) => String(x.CountryID) === String(selected));
              return c?.CountryName ?? String(selected);
            }}
          >
            <MenuItem value="">
              <em>All destinations</em>
            </MenuItem>
            {countries.map((c) => (
              <MenuItem key={c.CountryID} value={String(c.CountryID)}>
                {c.CountryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" variant="outlined" sx={{ ...inputSx(colors), minWidth: 220 }}>
          <InputLabel id="ea-exc" shrink>
            Excursion
          </InputLabel>
          <Select
            labelId="ea-exc"
            label="Excursion"
            value={excursionId}
            displayEmpty
            notched
            onChange={(e) => {
              setExcursionId(String(e.target.value));
              setPage(1);
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            inputProps={{ 'aria-label': 'Excursion' }}
            renderValue={(selected) => {
              if (selected === '' || selected == null) {
                return <Box component="span" sx={{ fontStyle: 'italic', opacity: 0.92 }}>All excursions</Box>;
              }
              const ex = excursions.find((x) => String(x.ExcursionID) === String(selected));
              return ex?.ExcursionName ?? String(selected);
            }}
          >
            <MenuItem value="">
              <em>All excursions</em>
            </MenuItem>
            {filteredExcursions.map((ex) => (
              <MenuItem key={ex.ExcursionID} value={String(ex.ExcursionID)}>
                {ex.ExcursionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Departure date"
          type="date"
          value={departureDate}
          onChange={(e) => {
            setDepartureDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ ...inputSx(colors), minWidth: 160 }}
        />

        <Button
          variant="outlined"
          startIcon={<ClearAllOutlinedIcon />}
          onClick={clearFilters}
          sx={{
            alignSelf: 'center',
            borderColor: colors.grey[500],
            color: colors.grey[100],
            '&:hover': { borderColor: colors.grey[400], backgroundColor: `${colors.primary[500]}88` },
          }}
        >
          Clear filters
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={2}>
        <Chip
          label={`Total persons: ${stats.totalPersons}`}
          sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], fontWeight: 600 }}
        />
        <Chip
          label={`Scanned: ${stats.scannedPersons}`}
          color="success"
          sx={{ fontWeight: 600 }}
        />
        <Chip
          label={`Not scanned: ${stats.nonScannedPersons}`}
          color="error"
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="caption" sx={{ color: colors.grey[400], alignSelf: 'center' }}>
          * Persons = total quantity across all booking rows
        </Typography>
      </Box>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <Box
        height="640px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}

          page={page - 1}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={(model) => {
            setFilterModel(model);
            setPage(1);
          }}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          loading={loading}
        />
      </Box>
    </Box>
  );
};

export default ExcursionAttendanceReport;
